import { useMemo } from 'react'

import { SvgIcon, Typography } from '@mui/material'
import { AvcInstance, AvcInstanceCapabilities, AvcInstanceHealthEnum } from '@nativewaves/nw-cloud-sdk/content'
import { ColumnDef } from '@tanstack/react-table'
import { Engine } from 'emotion-icons/fluentui-system-filled'
import { useTranslation } from 'react-i18next'

import { CopyId } from '@shared/components/Table/Fields'
import { getHealthColor } from 'utils'

export const useColumns = () => {
  const { t } = useTranslation(['common', 'domain'])

  const healthDescription = useMemo(
    () => ({
      [AvcInstanceHealthEnum.Ok]: t('domain:ContentProcessing.Instance.healthDescriptionOk'),
      [AvcInstanceHealthEnum.Warning]: t('domain:ContentProcessing.Instance.healthDescriptionWarning'),
      [AvcInstanceHealthEnum.Critical]: t('domain:ContentProcessing.Instance.healthDescriptionCritical'),
      [AvcInstanceHealthEnum.Offline]: t('domain:ContentProcessing.Instance.healthDescriptionOffline'),
    }),
    [t],
  )

  return useMemo<ColumnDef<AvcInstance>[]>(
    () => [
      {
        accessorKey: 'name',
        header: t('name'),
        meta: {
          sizing: {
            px: 2,
            minWidth: 480,
          },
        },
        cell: ({ getValue }) => getValue<string>() || `${t('shared.noNameSpecified')}`,
      },
      {
        accessorKey: 'health',
        header: t('status'),
        meta: {
          sizing: {
            px: 2,
            width: 180,
          },
          align: 'center',
        },
        cell: ({ getValue }) => (
          <Typography
            sx={[
              {
                width: 120,
                textAlign: 'center',
                display: 'inline-block',
                px: 0.5,
                py: 0.3,
                m: 0,
                backgroundColor: (theme) => getHealthColor(getValue<AvcInstance['health']>(), theme.palette.mode),
                color: (theme) =>
                  theme.palette.getContrastText(getHealthColor(getValue<AvcInstance['health']>(), theme.palette.mode)),
                borderRadius: 1,
              },
            ]}
            variant="text-sm"
          >
            {healthDescription[getValue<AvcInstance['health']>()]}
          </Typography>
        ),
      },
      {
        accessorKey: 'id',
        header: t('id'),
        meta: {
          sizing: {
            width: 200,
            px: 2,
          },
        },
        cell: ({ getValue }) => <CopyId id={getValue<string>()} />,
      },
      {
        accessorKey: 'capabilities',
        header: () => (
          <Typography sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="inherit" component="span">
              Engine
            </Typography>
            <SvgIcon sx={{ ml: 1 }} fontSize="small">
              <Engine />
            </SvgIcon>
          </Typography>
        ),
        meta: {
          sizing: {
            width: 180,
          },
        },
        cell: ({ getValue }) => {
          const info = getValue<AvcInstanceCapabilities>().engine
          if (!info || info.type?.toLocaleLowerCase() === 'unknown') return
          return `${info.type} ${info.version === 'unknown' ? '' : `(v-${info.version})`}`
        },
      },
    ],
    [healthDescription, t],
  )
}
